import { memo, useEffect, useRef } from "react";
import Grid from "@mui/material/Grid";
import QuantityInput from "../../common/QuantityInput";
import {
  maxLength,
  minLength,
  minValue,
  number,
  required,
  TextInput,
  useDataProvider,
} from "react-admin";
import InputAdornment from "@mui/material/InputAdornment";
import { useFormContext, useWatch } from "react-hook-form";
import DependOnAutocompleteInput from "../../../common/DependOnAutocompleteInput";
import SaleDependOnCustomer from "../../common/SaleDependOnCustomer";
import parserFormatNumInput from "../../../../utils/parserFormatNumInput";
import WarehouseReference from "../../common/WarehouseReference";
import useEnterToAddRowAndFocus from "../../../../utils/useEnterToAddRowAndFocus";

const equalLessThan = (a, b, c) => {
  const gv = c.source;
  const bv = +gv.split(".")[1];
  if (a > b.sale_return_items[bv].sent_quantity) {
    return "Must be equal or less than sent_quantity";
  }
  return undefined;
};

const validateSaleOrder = [required(), minLength(36), maxLength(36)];
const validateLocation = [required(), minLength(36), maxLength(36)];
const returnQuantityValidation = [
  required(),
  number(),
  minValue(0),
  equalLessThan,
];

const CstInput = ({
  returnQuantity,
  sentQuantity,
  saleIdSource,
  saleItemIdSource,
  returnQuantitySource,
  sentQuantitySource,
  nameSource,
  priceSource,
  locationSource,
}) => {
  const { setValue } = useFormContext();
  const dataProvider = useDataProvider();

  // 🔹 Ref ke input return_quantity
  const returnQuantityRef = useRef(null);
  // 🔹 Fungsi auto-tambah baris saat tekan Enter
  useEnterToAddRowAndFocus(returnQuantityRef, undefined, 'input[name*="sale_id"]');

  const dependOnVal = useWatch({ name: saleItemIdSource });
  useEffect(() => {
    if (!dependOnVal || dependOnVal.length !== 36) return;
    (async () => {
      try {
        const { name, price, sent_quantity } = await dataProvider.getListNoParams(
          "sale-returns/code-detail?id=" + encodeURIComponent(dependOnVal)
        );
        setValue(nameSource, name);
        setValue(sentQuantitySource, sent_quantity);
        setValue(priceSource, price.sell_price);
      } catch (error) {
        // optional: console.warn(error);
      }
    })();
  }, [dependOnVal]);

  return (
    <Grid
      container
      spacing={2}
      sx={{
        "& .MuiGrid-item": { py: 0 },
        mt: 0,
      }}
    >
      {/* Kolom kode penjualan */}
      <Grid item xs={12} lg={2.2}>
        <SaleDependOnCustomer
          variant="outlined"
          source={saleIdSource}
          label="mine.label.sale_code"
          isRequired
          fullWidth
          validate={validateSaleOrder}
          optionText="code"
        />
      </Grid>

      {/* Kolom item penjualan */}
      <Grid item xs={12} lg={1.7}>
        <DependOnAutocompleteInput
          variant="outlined"
          source={saleItemIdSource}
          label="mine.label.sale_item"
          isRequired
          fullWidth
          optionText="code"
          validate={validateSaleOrder}
          dependOn={saleIdSource}
          queryName="sale_id"
          resource="sale-items"
          helperText={false}
        />
      </Grid>

      {/* Kolom nama produk */}
      <Grid item xs={12} lg={2.9}>
        <TextInput
          variant="outlined"
          source={nameSource}
          label="mine.label.name"
          readOnly
          fullWidth
          helperText={false}
        />
      </Grid>

      {/* Kolom harga */}
      <Grid item xs={12} lg={1.7}>
        <TextInput
          variant="outlined"
          source={priceSource}
          label="mine.label.price"
          fullWidth
          readOnly
          InputProps={{
            startAdornment: <InputAdornment position="start">Rp</InputAdornment>,
            endAdornment: <InputAdornment position="end">/Box</InputAdornment>,
          }}
          {...parserFormatNumInput}
          helperText={false}
        />
      </Grid>

      {/* Kolom gudang */}
      <Grid item xs={12} lg={1}>
        <WarehouseReference
          variant="outlined"
          source={locationSource}
          label="mine.label.warehouse"
          fullWidth
          validate={validateLocation}
          reference="warehouses"
          orderBy="created_at"
          searchBy="name"
          dependOn="type"
          helperText={false}
        />
      </Grid>

      {/* Kolom jumlah dikirim */}
      <Grid item xs={12} lg={1.2}>
        <QuantityInput
          variant="outlined"
          source={sentQuantitySource}
          label="mine.label.sent_quantity"
          fullWidth
          readOnly
          quantity={returnQuantity}
          helperText={false}
        />
      </Grid>

      {/* Kolom jumlah retur */}
      <Grid item xs={12} lg={1.2} ref={returnQuantityRef}>
        <QuantityInput
          variant="outlined"
          source={returnQuantitySource}
          label="mine.label.return_quantity"
          fullWidth
          validate={returnQuantityValidation}
          quantity={returnQuantity}
          helperText="Bisa diisi 0 untuk SJ utama/gratis"
        />
      </Grid>
    </Grid>
  );
};

// 🔹 Cegah re-render tidak perlu
const propsAreEqual = (prevProps, nextProps) => {
  if (
    prevProps.formData.customer_id === nextProps.formData.customer_id &&
    prevProps.sale_id === nextProps.sale_id &&
    prevProps.sale_item_id === nextProps.sale_item_id &&
    prevProps.return_quantity === nextProps.return_quantity &&
    prevProps.warehouse_id === nextProps.warehouse_id &&
    prevProps.saleIdSource === nextProps.saleIdSource
  ) {
    return true;
  }
  return false;
};
const MemoizedInput = memo(CstInput, propsAreEqual);

// 🔹 Wrapper untuk digunakan di ArrayInput
const Inputs = ({ formData, scopedFormData, getSource, ...rest }) => {
  return (
    <MemoizedInput
      {...scopedFormData}
      formData={formData}
      saleIdSource={getSource("sale_id")}
      saleItemIdSource={getSource("sale_item_id")}
      returnQuantitySource={getSource("return_quantity")}
      sentQuantitySource={getSource("sent_quantity")}
      nameSource={getSource("name")}
      priceSource={getSource("price")}
      locationSource={getSource("warehouse_id")}
    />
  );
};

export default Inputs;
